import { forwardRef, useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import {
  getResolvedBondConfig,
  setBondBidResult,
  BOND_FINAL_STEP_KEY,
  type BondFieldOption,
  type BondSubmission,
} from "@/lib/brokerBonds";
import {
  bidExplanationRequired,
  bidGapPercent,
  bidResultLockReason,
  lostBidClosedOut,
  ourBidFromOrder,
  THRESHOLD_PERCENT,
} from "@/lib/bidResult";
import { formatBondValue } from "@/lib/bondValueDisplay";
import { anchorServerError } from "@/lib/serverFieldErrors";
import { NumericField } from "@/components/NumericField";
import { BidResultRecord } from "@/components/BidResultRecord";
import { buttonClass } from "@/components/buttonStyles";

// BRK-S183 → BRK-S422 — the bid result's REQUEST SUMMARY and its Won / Lost
// question, in ONE place: the bid result screen (`BidResultPage`) renders it
// as the page body, and the submission page's Bid Result dialog renders it
// in a dialog. The rules are `lib/bidResult.ts`'s; nothing here is forked.
//
// ── LOST: THE NEXT-LOWEST BID IS REQUIRED (FOR NOW) ──────────────────────────
//
// The pinned contract refuses a loss without it (`BID_AMOUNT_REQUIRED`), so the
// portal requires it until PMS-S488 / SUR drop the rule (Aaron 2026-09-26).
//
// ── LOST: THE EXPLANATION IS OPTIONAL UNTIL THE 10 % RULE (D7) ───────────────
//
// The wireframe's explanation is optional; decision D7 keeps the pinned
// `BID_EXPLANATION_REQUIRED` rule on top of it. `bidExplanationRequired`
// mirrors discovery's rule so the box turns required as the broker types
// (strictly more than 10 % below the next-lowest; exactly 10 % needs none),
// and the server's 422 renders inline when the two disagree. OUR BID comes
// from the order — the server's echo first, the filed bag second.
//
// ── LOST: CLOSED OUT ONLY WHEN THE SERVER SAYS SO ────────────────────────────
//
// "This request is closed and the capacity it was holding has been released"
// renders only when the order the server returned is `closed` with
// `closeReason: "not_awarded"` beside the lost result (`lostBidClosedOut`).
// Otherwise a saved loss reads as RECORDED and no capacity claim is made
// (NN #7).

export const BID_OUTCOMES = ["won", "lost"] as const;
export type BidOutcome = (typeof BID_OUTCOMES)[number];

/** The client's own required message for the next-lowest bid. */
const NEXT_LOWEST_REQUIRED =
  "Enter the next lowest bid — we need it to record a lost bid.";

/** The fields the lost write names in a refusal's `details.field`. */
const FIELDS = ["nextLowestBidAmount", "explanation"] as const;

/** The request summary's keys in the final step's bag. Read BY KEY — the
 *  labels are this screen's, fixed by the scope, not the configuration's. */
const SUMMARY_KEYS = {
  projectName: "projectName",
  obligeeName: "obligeeName",
  obligeeType: "obligeeType",
  bidDate: "bidDate",
  estimatedContractPrice: "estimatedContractPrice",
  bondPenalty: "bondAmount",
} as const;

function finalBag(order: BondSubmission): Record<string, unknown> {
  return order.values?.[BOND_FINAL_STEP_KEY] ?? {};
}

export function BidResultForm({
  order,
  onUpdated,
  offered,
  suggested,
  headingLevel = 2,
  onWon,
}: {
  order: BondSubmission;
  /** The order the lost write answered with (2xx only). */
  onUpdated: (order: BondSubmission) => void;
  /** `offersBidResult`'s answer; false says there is nothing to record. */
  offered: boolean;
  /** A deep link's `?result=` preselection. */
  suggested?: BidOutcome;
  /** The sections' heading level: 2 on the screen, 3 inside a dialog. */
  headingLevel?: 2 | 3;
  /** Set: Won goes straight on (the dialog). Unset: Won is a toggle with a
   *  Continue link (the screen). Either way NOTHING IS SAVED on Won. */
  onWon?: () => void;
}) {
  const { authedRequest } = useAuth();
  const Heading = headingLevel === 2 ? "h2" : "h3";
  const saved = order.bidResult ?? null;
  const lockReason = saved ? bidResultLockReason(order) : null;
  const wonPath = `/surety/submissions/${order.id}/won`;

  // A recorded result reads first; the chooser opens only on Update. A link's
  // `?result=` never opens it over a recorded result.
  const [editing, setEditing] = useState(saved === null);
  const [outcome, setOutcome] = useState<BidOutcome | "">(
    saved === null ? (suggested ?? "") : "",
  );
  const [nextLowest, setNextLowest] = useState<number | undefined>(
    saved?.nextLowestBidAmount ?? undefined,
  );
  const [explanation, setExplanation] = useState(saved?.explanation ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<Record<string, string>>({});
  const [justSaved, setJustSaved] = useState(false);
  const [obligeeTypeOptions, setObligeeTypeOptions] = useState<
    BondFieldOption[] | undefined
  >(undefined);

  // The obligee type's LABEL comes from the configuration's options (an option
  // value is not always its label). Non-fatal: without it the raw value reads.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const config = await getResolvedBondConfig(
          authedRequest,
          order.subproductId,
        );
        const field = config.finalStep.fields.find(
          (f) => f.key === SUMMARY_KEYS.obligeeType,
        );
        if (!cancelled) setObligeeTypeOptions(field?.options);
      } catch {
        if (!cancelled) setObligeeTypeOptions(undefined);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, order.subproductId]);

  // Keyboard and screen-reader users land ON the choice (the BRK-S177 promise):
  // the preselected button, else the first.
  const wonButton = useRef<HTMLButtonElement>(null);
  const lostButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!editing) return;
    (outcome === "lost" ? lostButton : wonButton).current?.focus();
    // Once, when the chooser opens — not on every outcome change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const ourBid = saved?.ourBidAmount ?? ourBidFromOrder(finalBag(order));
  const gap =
    outcome === "lost" &&
    ourBid !== null &&
    nextLowest !== undefined &&
    nextLowest > 0
      ? bidGapPercent(ourBid, nextLowest)
      : null;
  const explanationRequired = bidExplanationRequired({
    outcome: outcome === "" ? "won" : outcome,
    ourBidAmount: ourBid,
    nextLowestBidAmount: nextLowest,
    // Whether the rule APPLIES, so the box is marked before they type in it.
    explanation: null,
  });

  async function saveLost() {
    // REQUIRED for now (see the header): ask first rather than let a broker
    // hit the 422. The server's refusal still renders inline.
    if (nextLowest === undefined || !(nextLowest > 0)) {
      setError(null);
      setInvalid({ nextLowestBidAmount: NEXT_LOWEST_REQUIRED });
      return;
    }
    setBusy(true);
    setError(null);
    setInvalid({});
    try {
      const fresh = await setBondBidResult(authedRequest, order.id, {
        outcome: "lost",
        nextLowestBidAmount: nextLowest,
        ...(explanation.trim() ? { explanation: explanation.trim() } : {}),
      });
      onUpdated(fresh);
      setEditing(false);
      setJustSaved(true);
    } catch (err) {
      // `details.field` (singular) lands under its input; a 409 such as
      // `BID_RESULT_LOCKED` carries no field and renders as the line below.
      const anchored = anchorServerError(err, [...FIELDS]);
      setInvalid(anchored.byField);
      setError(anchored.summary);
    } finally {
      setBusy(false);
    }
  }

  const bag = finalBag(order);
  const obligeeName = formatBondValue(bag[SUMMARY_KEYS.obligeeName]);
  const obligeeType = formatBondValue(bag[SUMMARY_KEYS.obligeeType], {
    options: obligeeTypeOptions,
  });

  return (
    <>
      <section
        aria-labelledby="bid-request-summary"
        className="flex flex-col gap-4 rounded-xl border border-brk-border bg-brk-panel p-4 sm:p-6"
      >
        <Heading
          id="bid-request-summary"
          className="text-heading text-brk-text"
        >
          The request
        </Heading>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Fact
            label="Project"
            value={formatBondValue(bag[SUMMARY_KEYS.projectName])}
          />
          <Fact
            label="Obligee"
            value={
              obligeeName && obligeeType
                ? `${obligeeName} (${obligeeType})`
                : (obligeeName ?? obligeeType)
            }
          />
          <Fact
            label="Bid date"
            value={formatBondValue(bag[SUMMARY_KEYS.bidDate], { type: "date" })}
          />
          <Fact
            label="Estimated contract price"
            value={formatBondValue(bag[SUMMARY_KEYS.estimatedContractPrice], {
              type: "currency",
            })}
          />
          <Fact
            label="Bond penalty"
            value={formatBondValue(bag[SUMMARY_KEYS.bondPenalty], {
              type: "currency",
            })}
          />
        </dl>
      </section>

      {!offered ? (
        <p
          role="status"
          className="rounded-xl border border-brk-border bg-brk-panel p-4 text-sm text-brk-text sm:p-6"
        >
          There’s no bid result to record on this request.
        </p>
      ) : (
        <section
          aria-labelledby="bid-result-outcome"
          className="flex flex-col gap-5 rounded-xl border border-brk-border bg-brk-panel p-4 sm:p-6"
        >
          <Heading
            id="bid-result-outcome"
            className="text-heading text-brk-text"
          >
            {editing ? "How did this bid come out?" : "Recorded result"}
          </Heading>

          {/* The CLOSE-OUT, only on the server's word (see the header). */}
          {lostBidClosedOut(order) && (
            <p
              role="status"
              className="rounded-md bg-brk-bg p-3 text-sm text-brk-text"
            >
              This request is closed and the capacity it was holding has been
              released.
            </p>
          )}
          {justSaved && !lostBidClosedOut(order) && (
            <p role="status" className="text-sm text-brk-text">
              Your bid result is recorded.
            </p>
          )}

          {saved !== null && !editing && (
            <>
              <BidResultRecord result={saved} ourBid={ourBid} />
              <div className="flex flex-wrap items-center gap-3">
                {lockReason === null ? (
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(true);
                      setJustSaved(false);
                      // A low bidder (BRK-S402) is neither answer here: the
                      // broker picks won or lost afresh.
                      setOutcome(
                        saved.outcome === "low_bidder" ? "" : saved.outcome,
                      );
                    }}
                    className={buttonClass({
                      variant: "secondary",
                      size: "sm",
                    })}
                  >
                    Update
                  </button>
                ) : (
                  <p className="text-sm text-brk-dim">{lockReason}</p>
                )}
                {saved.outcome === "won" && (
                  <Link
                    to={wonPath}
                    className="text-sm text-brk-accent hover:underline"
                  >
                    Won bid details →
                  </Link>
                )}
              </div>
            </>
          )}

          {editing && (
            <div className="flex flex-col gap-4">
              <div
                role="group"
                aria-label="Bid outcome"
                className="grid grid-cols-1 gap-3 sm:grid-cols-2"
              >
                <ChoiceButton
                  ref={wonButton}
                  pressed={outcome === "won"}
                  onClick={() => {
                    setError(null);
                    setInvalid({});
                    if (onWon) onWon();
                    else setOutcome("won");
                  }}
                >
                  Won
                </ChoiceButton>
                <ChoiceButton
                  ref={lostButton}
                  pressed={outcome === "lost"}
                  onClick={() => setOutcome("lost")}
                >
                  Lost
                </ChoiceButton>
              </div>

              {outcome === "won" && (
                // WON SAVES NOTHING HERE: it continues to the won-bid screen,
                // where the final contract price and the P&P request are
                // confirmed (BRK-S184).
                <div className="flex flex-col items-start gap-3">
                  <p className="text-sm text-brk-dim">
                    Next, confirm the won bid’s details.
                  </p>
                  {onWon ? (
                    <button
                      type="button"
                      onClick={onWon}
                      className={buttonClass()}
                    >
                      Continue
                    </button>
                  ) : (
                    <Link to={wonPath} className={buttonClass()}>
                      Continue
                    </Link>
                  )}
                </div>
              )}

              {outcome === "lost" && (
                <>
                  <NumericField
                    kind="currency"
                    label="Next lowest bid"
                    value={nextLowest}
                    onChange={(value) => {
                      setNextLowest(value);
                      // The client's own "required" clears as they type; a
                      // server refusal is re-checked on the next save.
                      setInvalid((prev) => {
                        const next = { ...prev };
                        delete next.nextLowestBidAmount;
                        return next;
                      });
                    }}
                    required
                    error={invalid.nextLowestBidAmount}
                  />
                  {gap !== null && (
                    <p className="text-sm text-brk-dim">
                      {gap > 0
                        ? `Your bid is ${String(gap)}% below the next-lowest bid.`
                        : gap < 0
                          ? `Your bid was ${String(Math.abs(gap))}% above the next-lowest bid.`
                          : "Your bid matched the next-lowest bid."}
                    </p>
                  )}
                  <div className="flex w-full flex-col gap-1.5 text-left">
                    <label
                      htmlFor="bid-explanation"
                      className="text-sm font-medium text-brk-text"
                    >
                      Explanation
                      {explanationRequired && (
                        <span
                          aria-hidden="true"
                          className="ml-1 text-brk-accent"
                        >
                          *
                        </span>
                      )}
                    </label>
                    {explanationRequired && (
                      <p className="text-xs text-brk-dim">
                        More than {String(THRESHOLD_PERCENT)}% below the
                        next-lowest bid — tell us what you knew that the others
                        didn’t.
                      </p>
                    )}
                    <textarea
                      id="bid-explanation"
                      rows={3}
                      maxLength={2000}
                      value={explanation}
                      onChange={(e) => setExplanation(e.target.value)}
                      aria-required={explanationRequired || undefined}
                      aria-invalid={invalid.explanation ? true : undefined}
                      className="w-full rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
                    />
                    {invalid.explanation && (
                      <p role="alert" className="text-sm text-brk-danger">
                        {invalid.explanation}
                      </p>
                    )}
                  </div>
                </>
              )}

              {error && (
                <p role="alert" className="text-sm text-brk-danger">
                  {error}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-3">
                {outcome === "lost" && (
                  <button
                    type="button"
                    onClick={saveLost}
                    disabled={
                      busy || (explanationRequired && explanation.trim() === "")
                    }
                    className={buttonClass()}
                  >
                    {busy ? "Saving…" : "Record lost bid"}
                  </button>
                )}
                {saved !== null && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(false);
                      setError(null);
                      setInvalid({});
                    }}
                    className="text-sm text-brk-accent hover:underline"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          )}
        </section>
      )}
    </>
  );
}

function Fact({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-brk-dim">{label}</dt>
      <dd className="break-words font-medium text-brk-text">
        {value ?? <span className="font-normal text-brk-dim">Not stated</span>}
      </dd>
    </div>
  );
}

/** One of the two primary buttons. A toggle (`aria-pressed`) rather than a
 *  navigation, so a link's preselection reads as a pressed button and the
 *  broker still confirms. */
const ChoiceButton = forwardRef<
  HTMLButtonElement,
  { pressed: boolean; onClick: () => void; children: ReactNode }
>(function ChoiceButton({ pressed, onClick, children }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      // The two answers are equal choices: the pressed one carries the
      // view's accent, so a preselection reads at a glance.
      className={`${buttonClass({ variant: pressed ? "primary" : "secondary" })} h-auto py-3 text-base font-semibold`}
    >
      {children}
    </button>
  );
});
